// The package guide's Edit-plan zone row (SalesOrderPage ZoneEditRow): the number in the zone's box is in the
// unit the operator sees (finished ASSEMBLIES on an assembly zone, pieces otherwise), but the plan stores
// planned_box_zones.planned_qty in primary-part pieces, and jobq-api's POST /packaging/plans/{id}/zones/{id}/move
// takes `qty` in pieces too (MoveZoneRequest.qty: "only `qty` pieces are split off"). These helpers keep the
// two apart so a Move / Split compares and sends pieces (2026-10-27: a 4-per-assembly zone of 40 pcs showed
// "10"; Move compared 10 with 40 and split off 10 pcs instead of moving the whole zone).

/** {unitFactor, unitWord, displayPlanned}: pieces per unit the operator sees and the zone's planned count in it. */
export function zoneUnits(zone) {
  const isAssembly = !!(zone && zone.assembly_id && zone.assembly_name);
  const ppq = Math.max(1, parseInt(zone && zone.primary_part_quantity, 10) || 1);
  const unitFactor = isAssembly && ppq > 1 ? ppq : 1;
  return {
    unitFactor,
    unitWord: unitFactor > 1 ? 'assemblies' : 'pcs',
    displayPlanned: Math.floor(((zone && zone.planned_qty) || 0) / unitFactor),
  };
}

/**
 * What a Move-to does with the number typed in the zone's box (operator units).
 * Empty, zero, not a number, or the whole zone (or more) = move the WHOLE zone: {split: false, qty: undefined}.
 * Less than the whole zone = a split of that many units: {split: true, qty: <pieces to send>, moveUnits, keepUnits}.
 */
export function zoneMoveQty(zone, qtyInput) {
  const { unitFactor, unitWord, displayPlanned } = zoneUnits(zone);
  const plannedPcs = (zone && zone.planned_qty) || 0;
  const n = parseInt(qtyInput, 10);
  const whole = { split: false, qty: undefined, unitFactor, unitWord };
  if (!Number.isFinite(n) || n <= 0 || n >= displayPlanned) return whole;
  const pcs = n * unitFactor;
  if (pcs >= plannedPcs) return whole;
  return {
    split: true,
    qty: pcs,
    moveUnits: n,
    keepUnits: Math.floor((plannedPcs - pcs) / unitFactor),
    keepPcs: plannedPcs - pcs,
    unitFactor,
    unitWord,
  };
}

/** "3 assemblies (12 pcs)" on an assembly zone, "12 pcs" otherwise. */
function unitsText(units, pcs, unitFactor, unitWord) {
  return unitFactor > 1 ? `${units} ${unitWord} (${pcs} pcs)` : `${pcs} pcs`;
}

/** The Move-to dropdown's tooltip and first option for what is typed. */
export function zoneMoveText(zone, qtyInput) {
  const m = zoneMoveQty(zone, qtyInput);
  if (!m.split) {
    return {
      option: 'Move to…',
      title: 'Move this entire zone to another box/stack on this plan. To split off a smaller qty, '
        + `type the ${m.unitWord} you want to move in the field on the left first.`,
    };
  }
  return {
    option: `Split ${m.moveUnits} → …`,
    title: `Split off ${unitsText(m.moveUnits, m.qty, m.unitFactor, m.unitWord)} into another box/stack on this plan; `
      + `this zone keeps ${unitsText(m.keepUnits, m.keepPcs, m.unitFactor, m.unitWord)}.`,
  };
}
